import React, { useState, useEffect } from "react";
import { FoundationsButton } from "../../GenericComponents/button";
import { ApiDTO_PSFSform, DTO_PSFSCaseNote, useSaveCommentsMutation, useGetCommentsMutation } from "./Psfs_Slice";
declare let window: any
const UnicornApp = window.unicornInstance as any
import moment from "moment-timezone";

// ============ useCommentBox Hook ============
interface UseCommentBoxProps {
  form: ApiDTO_PSFSform;
  setForm: React.Dispatch<React.SetStateAction<ApiDTO_PSFSform>>;
  currentUserName?: string;
  currentUserId?: number;
  timezone?: string;
  caseId?: string;
  commentsReload?: any; // Dependency to trigger comments reload, can be a timestamp or a counter
}

interface UseCommentBoxReturn {
  newComment: string;
  setNewComment: React.Dispatch<React.SetStateAction<string>>;
  error: boolean;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  handleCommentSubmit: (e?: React.FormEvent) => Promise<void>;
  handleCommentEdit: (index: number, newNote: string) => void;
  isLoading: boolean;
  commentBoxProps: {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onSubmit: (e?: React.FormEvent) => void;
    onEdit: (index: number, newNote: string) => void;
    commentsList: DTO_PSFSCaseNote[] | undefined;
    currentUserId: number;
    error: boolean;
  };
}

/**
 * Custom hook to manage CommentBox state and handlers
 * Implements DRY principle by centralizing comment management logic
 */
export const useCommentBox = ({ form, caseId, setForm,commentsReload, currentUserName = '', currentUserId, timezone }: UseCommentBoxProps): UseCommentBoxReturn => {
  const [newComment, setNewComment] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [internalCurrentUserId, setInternalCurrentUserId] = useState<number>(currentUserId ?? -1);
  const [internalCurrentUserName, setInternalCurrentUserName] = useState<string>(currentUserName);
  const [SaveComments] = useSaveCommentsMutation();
  const [GetComments] = useGetCommentsMutation();

  React.useEffect(() => {
    setNewComment("");
    setError(false);
  }, [form.Comments]);

  // Fetch current user and comments on mount
  useEffect(() => {
    UnicornApp.getCurrentUserFromAPI()
      .then((me: any) => {
        if (me) {
          setInternalCurrentUserId(me.attributes.id);
          setInternalCurrentUserName(`${me.attributes.firstname} ${me.attributes.lastName}`);
        }
      })
      .catch((error: any) => {
        console.error('Error fetching current user:', error);
        setInternalCurrentUserId(0);
      });

    if (caseId && typeof caseId === 'string' && caseId !== 'undefined') {
      setIsLoading(true);
      GetComments(caseId)
        .unwrap()
        .then((response) => {
          const comments = response as DTO_PSFSCaseNote[];
          if (comments) {
            setForm((prev: ApiDTO_PSFSform) => ({
              ...prev,
              Comments: comments
            }));
          }
        })
        .catch((error) => {
          console.error('Error fetching comments:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [ caseId, GetComments, setForm, commentsReload]);

  const handleCommentSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (isLoading) return;

    if (newComment.trim() === '') {
      setError(true);
      return;
    }

    setError(false);
    setIsLoading(true);

    const newCaseNote: Partial<DTO_PSFSCaseNote> = {
      Id: -1,
      CaseId: form.CaseId,
      ts_CreationDateTime: moment.tz("America/New_York").format(), // Store as EST ISO string
      PostedByUserId: internalCurrentUserId ?? form.fk_CreatedBy,
      PostedByUserFullName: internalCurrentUserName,
      Note: newComment?.trim(),
      tzCode: timezone || form.tzCode || '',
      NoteType: 'PSFS',
      ExamCompleted: false,
      isChanged: false
    };

    try {
      const saved = await SaveComments(newCaseNote).unwrap();
      if (saved) {
        setForm((prev: ApiDTO_PSFSform) => ({
          ...prev,
          Comments: [...(prev.Comments || []), saved],
          fk_LatestComment: saved.Id // Set the ID of the newly added comment
        }));
        setNewComment("");
      }
    } catch (error) {
      console.error('Error saving comment:', error);
    }
    finally {
      setIsLoading(false); // <-- Reset loading
    }
  };

  const handleCommentEdit = async (index: number, newNote: string) => {
    setForm((prev: ApiDTO_PSFSform) => {
      const updatedComments = [...(prev.Comments || [])];
      if (updatedComments[index]) {
        updatedComments[index] = {
          ...updatedComments[index],
          Note: newNote,
          UpdatedByUserId: internalCurrentUserId ?? form.fk_UpdatedBy,
          UpdatedByUserFullName: internalCurrentUserName,
          isChanged: true
        };
      }
      return {
        ...prev,
        Comments: updatedComments
      };
    });

    // Save only the edited comment
    const editedComment = {
      ...form.Comments?.[index],
      Id: form.Comments?.[index]?.Id,
      Note: newNote,
      UpdatedByUserId: internalCurrentUserId ?? form.fk_UpdatedBy,
      UpdatedByUserFullName: internalCurrentUserName,
      isChanged: true
    };

    try {
      await SaveComments(editedComment).unwrap();
      // Optionally, refresh comments from server here if needed
    } catch (error) {
      console.error('Error saving edited comment:', error);
    }
  };

  const commentBoxProps = {
    value: newComment,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNewComment(e.target.value);
      if (error && e.target.value.trim() !== '') {
        setError(false);
      }
    },
    onSubmit: handleCommentSubmit,
    onEdit: handleCommentEdit,
    commentsList: form.Comments,
    currentUserId: internalCurrentUserId ?? form.fk_CreatedBy,
    error: error,
    isLoading
  };

  return {
    newComment,
    setNewComment,
    error,
    setError,
    handleCommentSubmit,
    handleCommentEdit,
    isLoading,
    commentBoxProps
  };
};

// ============ CommentBox Component ============
interface CommentBoxProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (e?: React.FormEvent) => void;
  onEdit?: (index: number, newNote: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  commentsList?: DTO_PSFSCaseNote[];
  currentUserId?: number;
  minHeight?: string;
  isLoading?: boolean;
}

const CommentBox: React.FC<CommentBoxProps> = ({
  value,
  onChange,
  onSubmit,
  onEdit,
  placeholder = "New Comment",
  disabled = false,
  error = false,
  commentsList = [],
  currentUserId,
  minHeight = "445px",
  isLoading = false,
}) => {
  const [isEditMode, setIsEditMode] = React.useState<boolean>(false);
  const [editValues, setEditValues] = React.useState<{ [key: number]: string }>({});
  const [isCustomerSite] = useState<boolean>(UnicornApp.checkUserPrivileges("NTSPAdmin"))
  const [errors, setErrors] = useState<{ [index: number]: string }>({});

  React.useEffect(() => {
    setIsEditMode(false);
    setEditValues({});
    setErrors({});
  }, [commentsList]);

  const handleEditModeToggle = () => {
    if (!isEditMode) {
      const initialValues: { [key: number]: string } = {};
      commentsList.forEach((item, idx) => {
        initialValues[idx] = item.Note;
      });
      setEditValues(initialValues);
    }
    setIsEditMode(!isEditMode);
  };

  const handleEditChange = (index: number, newValue: string) => {
    setEditValues(prev => ({
      ...prev,
      [index]: newValue
    }));

    // Validation: show error if empty
    setErrors(prev => ({
      ...prev,
      [index]: newValue.trim() === "" ? "Edited comment cannot be empty" : ""
    }));
  };

  const handleSaveAll = () => {
    let hasError = false;
    const newErrors: { [index: number]: string } = {};

    Object.keys(editValues).forEach((key) => {
      const index = parseInt(key);
      const originalNote = commentsList[index]?.Note;
      const editedNote = editValues[index]?.trim();

      if (onEdit && editedNote !== undefined && editedNote !== originalNote) {
        if (editedNote === "") {
          hasError = true;
          newErrors[index] = "Edited comment cannot be empty";
        }
      }
    });

    setErrors(newErrors);

    if (hasError) return;

    Object.keys(editValues).forEach((key) => {
      const index = parseInt(key);
      const originalNote = commentsList[index]?.Note;
      const editedNote = editValues[index]?.trim();

      if (onEdit && editedNote && editedNote !== originalNote) {
        onEdit(index, editedNote);
      }
    });

    setIsEditMode(false);
    setEditValues({});
    setErrors({});
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, index: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveAll();
    } else if (e.key === "Escape") {
      setIsEditMode(false);
      setEditValues({});
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditValues({});
    setErrors({});
  };

  const getDisplayName = (item: DTO_PSFSCaseNote, currentUserId: number) => {
    if (item.UpdatedByUserId && item.UpdatedByUserId !== -1) {
      return currentUserId === item.UpdatedByUserId
        ? 'Me (edited)'
        : `${item.UpdatedByUserFullName} (edited)`;
    }
    return currentUserId === item.PostedByUserId
      ? 'Me'
      : item.PostedByUserFullName;
  };

  return (
    <div className="second-child">
      <label htmlFor="Comments">Comments</label>
      <div className="mb-3 comments-section">
        <textarea
          id="Comments"
          className={`w-100 mt-2 ${error ? 'error-border' : ''}`}
          style={{ height: "6rem", borderRadius: "5px", borderColor: error ? 'red' : undefined }}
          name="Comments"
          maxLength={2048}
          placeholder={placeholder}
          value={
            Array.isArray(value)
              ? value.map((c: any) => c.Note ?? "").join("\n")
              : value || ""
          }
          onChange={onChange}
          tabIndex={8}
        />
        {error && <span style={{ color: 'red', fontSize: '12px' }}>Comment cannot be empty</span>}
        <FoundationsButton
          id="psfs_new_case"
          buttonClasses="app-button"
          onClick={onSubmit}
          disabled={!isCustomerSite || value.length === 0 || isEditMode || isLoading}
        >
          Submit
        </FoundationsButton>
      </div>

      <div className="psfs-text-container" style={{ minHeight: minHeight }}>
        {Array.isArray(commentsList) && commentsList.length > 0 && (
          <div className="psfs-comments-list" style={{ marginTop: '1rem' }}>
            <ul>
              {commentsList.sort(
                (a, b) =>
                  moment.tz(b.ts_CreationDateTime, "America/New_York").valueOf() -
                  moment.tz(a.ts_CreationDateTime, "America/New_York").valueOf()
              ).map((item: DTO_PSFSCaseNote, idx) => (
                <div
                  key={idx}
                  style={{
                    marginBottom: '18px',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ marginBottom: '4px', fontSize: '12px' }}>
                    {moment.tz(item.ts_CreationDateTime, "America/New_York")
                      .format('MM/DD/YYYY HH:mm')}
                  </div>
                  <div
                    style={{
                      display: 'inline-block',
                      width: '80%',
                      textAlign: 'left',
                      marginLeft: '0',
                      marginRight: '0',
                      wordBreak: 'break-word',
                    }}
                  >
                    <strong>{getDisplayName(item, currentUserId ?? -1)}</strong>
                    :{' '}
                    {isEditMode ? (
                      <textarea
                        ref={(el) => {
                          if (el) {
                            el.style.height = 'auto';
                            el.style.height = `${el.scrollHeight}px`;
                          }
                        }}
                        value={editValues[idx] ?? item.Note}
                        onChange={(e) => handleEditChange(idx, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, idx)}
                        rows={1}
                        style={{
                          width: '100%', // 🔑 important
                          minHeight: '32px',
                          resize: 'none',
                          overflow: 'hidden',
                          lineHeight: '1.4',
                          boxSizing: 'border-box',
                          borderRadius: '5px'
                        }}
                        maxLength={2048}
                        onInput={(e) => {
                          const target = e.target as HTMLTextAreaElement;
                          target.style.height = 'auto';
                          target.style.height = `${target.scrollHeight}px`;
                        }}
                      />
                    ) : (
                      <span>{item.Note}</span>
                    )}
                    {errors[idx] && (
                      <div
                        style={{
                          color: 'red',
                          position: 'absolute',
                          lineHeight: '1',
                          fontSize: "smaller",
                          margin: '3px'
                        }}
                      >
                        {errors[idx]}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </ul>
          </div>
        )}
      </div>

      <span style={{ display: "flex", justifyContent: "end", gap: "8px" }}>
        {isEditMode ? (
          <>
            <button type="button" className="text-edit-btn" onClick={handleSaveAll} style={{
              color: 'green',
              cursor: Object.values(errors).some(e => !!e) ? 'not-allowed' : 'pointer'
            }} disabled={!isCustomerSite || Object.values(errors).some(e => !!e)}>
              (Save All)
            </button>
            <button type="button" className="text-edit-btn" onClick={handleCancel} style={{ color: 'red' }} disabled={!isCustomerSite}>
              (Cancel)
            </button>
          </>
        ) : (
          <button type="button" className="text-edit-btn" onClick={handleEditModeToggle} title="Edit all comments" style={{ cursor: !isCustomerSite || Object.values(errors).some(e => !!e) || commentsList.length == 0 ? 'not-allowed' : 'pointer' }} disabled={!isCustomerSite || Object.values(errors).some(e => !!e) || commentsList.length == 0}
          >
            (Edit)
          </button>
        )}
      </span>
    </div>
  );
};

export default CommentBox;